import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { dateLabel } from '@adisys/shared';
import { Button, Skeleton } from '../components/ui';
import { ProposalDocument } from './crm/ProposalDocument';
import { amountInWords } from './crm/QuotationDocument';

/**
 * What a customer sees from the emailed link. It signs nobody in and calls only the
 * public endpoint, so it works in a browser that has never heard of FieldOps.
 */
export function PublicProposalPage() {
  const { token } = useParams();
  const { data: q, isLoading, error } = useQuery({
    queryKey: ['public-proposal', token], retry: false, refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await fetch(`/api/public/proposals/${encodeURIComponent(token ?? '')}`, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(res.status === 404 ? 'not-found' : 'failed');
      return res.json();
    },
  });

  useEffect(() => {
    const m = document.createElement('meta'); m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  if (isLoading) return <div className="mx-auto max-w-3xl p-8"><Skeleton className="h-96 w-full" /></div>;

  if (error || !q) {
    const missing = (error as Error | null)?.message === 'not-found';
    return (
      <div className="mx-auto mt-24 max-w-md rounded-xl bg-white p-8 text-center shadow-card">
        <h1 className="text-lg font-semibold text-ink-900">{missing ? 'This link is no longer valid' : 'We could not load this proposal'}</h1>
        <p className="mt-2 text-sm text-ink-600">
          {missing ? 'It may have expired or been copied incorrectly. Please ask your ADISYS contact to send it again.'
            : 'Please try again in a moment, or contact your ADISYS representative.'}
        </p>
      </div>
    );
  }

  const outdated = ['superseded', 'rejected'].includes(q.status);
  const sp = q.salesperson;

  return (
    <div className="min-h-dvh bg-ink-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print { .proposal-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      `}</style>
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-line bg-card px-4 py-2.5">
        <p className="min-w-0 truncate text-sm font-medium text-ink-800">{q.quoteNo} · {q.customer?.company}</p>
        <span className="hidden text-xs text-ink-500 md:inline">In the print dialog set Margins to None and untick “Headers and footers”.</span>
        <Button size="sm" variant="primary" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>Print / Save as PDF</Button>
      </div>
      {outdated && (
        <div className="no-print mx-auto mt-4 max-w-[210mm] rounded-lg bg-warning-soft px-4 py-3 text-sm text-ink-800">
          <strong>This version has been {q.status === 'superseded' ? 'replaced by a newer one' : 'withdrawn'}.</strong>{' '}
          Please contact {sp?.name ?? 'your ADISYS representative'}{sp?.email ? ` (${sp.email})` : ''} for the current proposal.
        </div>
      )}
      <ProposalDocument q={q} amountInWords={amountInWords} readOnly />
      <p className="no-print pb-8 text-center text-xs text-ink-500">This private link expires on {dateLabel(q.linkExpiresAt)}.</p>
    </div>
  );
}
